/**
 * The Overview's dials (DESIGN UD-36, 2026-10-19): the authorship dial reads the calibrated value as a confidence, always
 * with its conditions; the translation dial is an uncalibrated measure and never reads as one. Same conventions as
 * `strings-explain.ts`: plain constants and small functions, no DOM; none may contain a word matched by `BANNED_WORDS`
 * (test/unit/dial.test.ts). Every figure arrives formatted from the calibrated index or the model card; nothing is computed
 * here. "in 100" and "confidence" occur only in the authorship dial's strings, and only with the conditions beside them.
 */

export const DIAL = {
	/** The ends of both dials. */
	humanEnd: 'human-like',
	machineEnd: 'machine-like',
	translationLowEnd: 'fewer marks',
	translationHighEnd: 'more marks',
	authorship: {
		/** The large figure under the dial. */
		reading: (n: string) => `${n} in 100`,
		/** The B sentence (UD-36): the reading and the prior it assumes. */
		sentence: (n: string) =>
			`Among documents of our reference corpus that score like this one, about ${n} in 100 were machine-written, assuming that half the documents are machine-written.`,
		/** Beside the reading, always (UD-36 conditions 1 and 2). */
		conditionsLabel: 'Conditions of this reading',
		notThisDocument:
			'This is a reading of the score on our reference corpus, not how sure to be that this one document is machine-written. That depends on how much machine text you receive: the less of it, the more of the documents that score like this one are human.',
		corpus:
			'The reference corpus holds human texts, and machine texts from Anthropic models only, in English only; text from other models, and machine text that a person has edited, is not measured.',
		corpusLink: 'Model card',
		/** `group` is the length group id ("B1–B2"); `ece` and `upper` are in 100 (one decimal). */
		calibrationError: (group: string, ece: string, upper: string) =>
			`Calibration error for length group ${group}: ${ece} in 100 on average (95% upper limit ${upper} in 100), measured out of fold.`,
		noCalibrationError: 'No calibration error is recorded for this length group in the model card.',
		intervalNote:
			'The shaded arc is the text-sampling variation of this document only; it leaves out the calibration error above.',
		referenceMark: (point: string) =>
			`Mark at ${point}: the reference point; an index above it is flagged.`,
		ceilingMark: (ceiling: string) =>
			`Mark at ${ceiling}: the highest index a text of this length can reach.`,
		/** Greyed state: the lead of the reason. */
		noReading: 'No reading',
		greyedLead: 'The dial has no needle because',
		ariaLead: 'Dial from human-like to machine-like.',
		ariaNeedle: (n: string, sentence: string) => `Needle at ${n} of 100. ${sentence}`,
		ariaGreyed: (reason: string) => `No needle: ${reason}`,
		ariaInterval: (lo: string, hi: string) =>
			`Text-sampling variation of this document: ${lo} to ${hi}.`,
	},
	translation: {
		label: 'Uncalibrated: a measure of translation marks, not a confidence.',
		ariaLead: 'Smaller dial from fewer to more translation marks.',
		ariaNeedle: (n: string) =>
			`Needle at ${n} of 100. Uncalibrated: a measure of translation marks, not a confidence.`,
		ariaGreyed: 'No needle: the translation index could not be measured for this text.',
	},
} as const;
